import { CellKeyDownEvent, Events, GridApi } from '@ag-grid-community/core'

/**
 * Enable
 *  - ctrl + c keyboard shortcut to copy selected cells into the clipboard
 *    : If only one cell is selected, copy only the cell value.
 *    : If multiple rows of the same column are selected, copy only the cell values (more convenient for users if they want to paste into a quick filter)
 *    : If multiple rows of multiple columns are selected, copy both the header and the cell values.
 
 *  - ctrl + shift + c keyboard shortcut to copy selected cells into the clipboard
 *    : If only one cell is selected, copy only the cell value.
 *    : If more than one cell is selected, copy the header and the cell values.
 *
 * Usage: call this from onGridReady:
 * <pre> *   onGridReady: ({ api, columnApi }) => {
 *     ...other init stuff...
 *     enableHeliosCopyBehavior(api)
 *   },
 *
 * </pre>
 */
export function enableHeliosCopyBehavior(api: GridApi) {
  api.addEventListener(Events.EVENT_CELL_KEY_DOWN, (e: CellKeyDownEvent) => {
    const event = e.event as KeyboardEvent
    const hasMoreThanOneCellSelected = false
    const columnsSelected = api
      .getCellRanges()
      ?.map(range => range.columns.length)[0] as number
    const rowsSelected = numberOfRowsSelected(api) as number

    if ((event.ctrlKey || event.metaKey) && event.key == 'c') {
      if (columnsSelected < 2) {
        api.copySelectedRangeToClipboard(hasMoreThanOneCellSelected)
      } else {
        api.copySelectedRangeToClipboard(!hasMoreThanOneCellSelected)
      }
    }

    if (
      (event.ctrlKey || event.metaKey) &&
      event.shiftKey &&
      event.key == 'c'
    ) {
      if (rowsSelected < 2 && columnsSelected < 2) {
        api.copySelectedRangeToClipboard(hasMoreThanOneCellSelected)
      } else {
        api.copySelectedRangeToClipboard(!hasMoreThanOneCellSelected)
      }
    }
  })
}

/**
 * Find selected cell ranges without selecting the rows
 *
 * @internal testing only
 */
export function numberOfRowsSelected(api: GridApi) {
  const cellRanges = api.getCellRanges()
  if (!cellRanges || cellRanges.length < 1) return
  const startRow = cellRanges[0].startRow?.rowIndex
  const endRow = cellRanges[0].endRow?.rowIndex

  if (startRow == undefined || endRow == undefined) return
  if (startRow == endRow) return 1

  return endRow - startRow + 1
}
